import { useEffect, useState } from 'react';
import { apiUrl } from './config/api';
import { authFetch, MIN_PASSWORD_LENGTH } from './config/auth';
import useFocusTrap from './useFocusTrap';
import AvButton from './design/nedi/AvButton';
import AvField from './design/nedi/AvField';
import { NediPanel, NediScreen } from './design/nedi/NediLayout';
import { mountNediTheme } from './design/nedi/mountNediTheme';
import './design/atlasVivo/tokens.css';
import './design/atlasVivo/Buttons.css';
import './LoginModal.css';
import { t } from './i18n';

function submitClosestForm(e) {
  const form = e.currentTarget.closest('form');
  if (form?.requestSubmit) form.requestSubmit();
  else form?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
}

function ChangePasswordModal({ onClose, onSuccess }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const containerRef = useFocusTrap(true, onClose);

  useEffect(() => {
    mountNediTheme();
  }, []);

  useEffect(() => {
    let cancelled = false;
    authFetch(apiUrl('/api/auth/me'))
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!cancelled && data?.email) setEmail(data.email);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setEmailError('');
    setEmailSuccess('');
    setEmailLoading(true);
    try {
      const response = await authFetch(apiUrl('/api/auth/me'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await response.json();
      if (response.ok) {
        setEmailSuccess(t('web.account.emailSaved'));
      } else {
        setEmailError(data.error || t('web.account.emailSaveFail'));
      }
    } catch {
      setEmailError(t('web.account.serverError'));
    } finally {
      setEmailLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setErrorMessage(t('web.account.fillAll'));
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setErrorMessage(t('web.account.passwordMin', { n: MIN_PASSWORD_LENGTH }));
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage(t('web.account.passwordMismatch'));
      return;
    }

    setLoading(true);
    try {
      const response = await authFetch(apiUrl('/api/auth/change-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setSuccessMessage(data.message || 'Senha alterada com sucesso.');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        if (onSuccess) onSuccess(data);
      } else {
        setErrorMessage(data.error || t('web.account.passwordChangeFail'));
      }
    } catch {
      setErrorMessage(t('web.account.serverError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-modal-overlay" onClick={onClose} role="presentation">
      {/* Package CEs: deepen 5.2 Timeline React beyond LoginModal (account shell). */}
      <NediScreen className="login-nedi-screen">
        <div
          className="login-dialog"
          ref={containerRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="change-password-title"
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        >
          <header className="login-header">
            <h1 id="change-password-title">Conta</h1>
          </header>

          <NediPanel className="login-nedi-panel">
            <form onSubmit={handleEmailSubmit} className="login-form">
              <AvField
                name="account-email-input"
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="seu@email.com"
                autoComplete="email"
              />
              <p className="login-info-text">
                Usado para redefinir sua senha por email caso você a esqueça.
              </p>
              {emailError && (
                <div className="login-error-msg" role="alert">{emailError}</div>
              )}
              {emailSuccess && (
                <div className="login-success-msg" role="status">{emailSuccess}</div>
              )}
              <AvButton
                type="button"
                variant="primary"
                disabled={emailLoading}
                loading={emailLoading}
                block
                onClick={submitClosestForm}
              >
                {emailLoading ? t('web.account.saving') : t('web.account.saveEmail')}
              </AvButton>
            </form>

            <h2 className="login-subheading">Alterar senha</h2>
            <form onSubmit={handleSubmit} className="login-form">
              <AvField
                name="current-password-input"
                label="Senha atual"
                type="password"
                value={currentPassword}
                onChange={setCurrentPassword}
                autoComplete="current-password"
                required
              />
              <AvField
                name="new-password-input"
                label="Nova senha"
                type="password"
                value={newPassword}
                onChange={setNewPassword}
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
                required
              />
              <AvField
                name="confirm-password-input"
                label="Confirmar nova senha"
                type="password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
                required
              />

              {errorMessage && (
                <div className="login-error-msg" role="alert">{errorMessage}</div>
              )}
              {successMessage && (
                <div className="login-success-msg" role="status">{successMessage}</div>
              )}

              <div className="login-actions-row">
                <AvButton type="button" variant="secondary" onClick={onClose} disabled={loading}>
                  {t('action.close')}
                </AvButton>
                <AvButton
                  type="button"
                  variant="primary"
                  disabled={loading}
                  loading={loading}
                  onClick={submitClosestForm}
                >
                  {loading ? t('web.account.saving') : t('web.account.savePassword')}
                </AvButton>
              </div>
            </form>
          </NediPanel>
        </div>
      </NediScreen>
    </div>
  );
}

export default ChangePasswordModal;
